<template>
  <div class="container-fluid">
    <page-header
      :page="page"
      has-return
      :left-side-class="'col-xl-8'"
      :right-side-class="
        'col-xl-4 align-items-end d-flex pb-2 justify-content-end'
      "
    >
      <template v-slot:customActions>
        <ActionButton
          v-if="userCanEdit"
          text="Guardar"
          variant="success m-1"
          i="fas fa-save"
          :responsive="false"
          @click="saveChanges"
        />
      </template>
    </page-header>

    <school-information
      ref="schoolInformationForm"
      v-model="model"
      :medicaid-enabled="isMedicaidDocumentationWorkflowEnabled"
      :class="['mb-4', { 'px-0': isMobile }]"
    />

    <medicaid-certification
      v-if="showMedicaidCertification"
      class="my-4"
      :school="model"
    />

    <configuration-by-academic-year
      class="pt-4"
      v-if="model.locationSchoolId"
      :location-school-id="model.locationSchoolId"
      :rows="pagination.data"
    />
    <pagination
      v-if="pagination.totalItemCount > pagination.count"
      :show-text="false"
      :data="pagination"
      :page-size="pagination.count"
      @pagination-go-page="getSchoolConfigs"
    />
  </div>
</template>

<script>
import SchoolInformation from './SchoolInformation';
import ConfigurationByAcademicYear from './ConfigurationByAcademicYear';
import MedicaidCertification from './MedicaidCertification';
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import Pagination from 'pagination';

import alertMixin from '@/mixin/alert.mixin';
import objectToFormData from 'object-to-formdata';

import { modulesName } from '@/modules';
import { createNamespacedHelpers } from 'vuex';
import {
  actionTypes,
  getterTypes,
} from '@/modules/operation-school-management/store/types';
const { mapActions, mapGetters } = createNamespacedHelpers(
  modulesName.operationSchoolManagementModuleName
);
import { getSysparameterByNames } from '@/modules/system-parameter-managment/api';
import schoolApi from '@/modules/school-management/api';

const EDIT_SCHOOL_ROUTE_NAME = 'AddEditSchool';
const SCHOOL_MANAGEMENT_ROUTE_NAME = 'schooltManagement';

export default {
  name: 'AddEditSchool',
  mixins: [alertMixin],
  components: {
    SchoolInformation,
    ConfigurationByAcademicYear,
    MedicaidCertification,
    PageHeader,
    ActionButton,
    Pagination,
  },
  data() {
    return {
      pageTitle: 'Crear Nueva Escuela',
      schoolIdToEdit: null,
      model: {
        locationSchoolId: 0,
        medicaidFlag: false,
        physicalState: { id: 0, name: 'PR' },
        rlvType: null,
        idearFlag: false,
        medicaidCertifications: [],
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 0,
        count: 3,
        data: [],
      },
      isMedicaidDocumentationWorkflowEnabled: false,
    };
  },
  computed: {
    ...mapGetters({
      filter: getterTypes.GET_FILTER,
    }),
    page() {
      const page = {
        icon: 'fad fa-school',
        title: 'Manejo de Escuelas',
        routes: ['Inicio', 'Operaciones', 'Manejo de Escuela', 'Crear Escuela'],
      };

      if (this.isEditMode) {
        page.routes = [
          'Inicio',
          'Operaciones',
          'Manejo de Escuela',
          'Editar Escuela',
        ];
      }

      return page;
    },
    isEditMode() {
      return Boolean(this.id);
    },
    userCanEdit() {
      return this.$store.getters.HasPermission('school.edit');
    },
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
    id() {
      return this.$route.params.id;
    },
    showMedicaidCertification() {
      return (
        this.isMedicaidDocumentationWorkflowEnabled && this.model.medicaidFlag
      );
    },
  },
  methods: {
    ...mapActions([actionTypes.SAVE_CHANGES, actionTypes.FETCH_SCHOOL_TYPES]),
    async saveChanges() {
      const isValid = await this.$refs.schoolInformationForm.$refs.generalInformationForm.validate();

      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor de completar la información solicitada para crear la escuela.',
          'error'
        );
        return;
      }

      const schoolInfo = this.model;

      const paramsToSend = {
        ...schoolInfo,
        schoolType: schoolInfo.schoolType?.id,
        providerId: schoolInfo.provider?.id,
        physicalState: schoolInfo.physicalState.name,
        rlvClassificationTypeId: schoolInfo.rlvType?.id,
        physicalMunicipality: schoolInfo.physicalMunicipality?.name,
        physicalZipCode: schoolInfo.physicalZipCode.split('-')[0],
        physicalZipCodePlus: schoolInfo.physicalZipCode.split('-')[1],
        casaFlag: schoolInfo.casaFlag ? '1' : '0',
        idearFlag: schoolInfo.idearFlag ? '1' : '0',
      };

      if (this.isEditMode) {
        paramsToSend.locationSchoolId = this.id;
      }

      const formData = objectToFormData(paramsToSend, { indices: true });

      if (schoolInfo.medicaidCertifications?.length) {
        schoolInfo.medicaidCertifications.forEach((doc, i) =>
          formData.append(`medicaidCertifications[${i}].file`, doc.file)
        );
      }

      const saveResult = await this[actionTypes.SAVE_CHANGES]({
        isEditMode: this.isEditMode,
        formData,
      });

      if (saveResult) {
        const savedId = saveResult.data.locationSchoolId || this.id;
        this.model = saveResult.data;
        this.ShowToast(
          'Proceso Exitoso.',
          'La escuela ha sido guardada exitosamente.',
          'success'
        );

        if (!this.isEditMode) {
          this.$router.push({
            name: EDIT_SCHOOL_ROUTE_NAME,
            params: {
              id: savedId,
            },
          });
          await this.$nextTick();
          await this.$refs.schoolInformationForm.getSchool(savedId);
        } else {
          this.goBack();
        }
      }
    },
    goBack() {
      this.$router.push({ name: SCHOOL_MANAGEMENT_ROUTE_NAME });
    },
    async getSchool() {
      if (!this.isEditMode) return;
      const { data } = await schoolApi.getSchoolData(this.id, {
        includeConfigs: false,
      });
      this.model = { ...data, casaFlag: data.casaFlag == '1' };
    },
    async getSchoolConfigs(currentPage) {
      if (!this.isEditMode) return;

      const { data: result } = await schoolApi.getSchoolConfigs(this.id, {
        currentPage,
        count: this.pagination.count,
      });
      result.data = (result.data ?? []).map((x) => ({ ...x, opened: false }));
      this.pagination = result;
    },
    async getMedicaidDocumentationWorkflowEnabledParam() {
      const { data } = await getSysparameterByNames({
        parameterNames: ['MedicaidDocumentationWorkflowEnabled'],
      });

      this.isMedicaidDocumentationWorkflowEnabled =
        data
          ?.find((x) => x.name == 'MedicaidDocumentationWorkflowEnabled')
          ?.staticValue?.toLowerCase() == 'true' ?? false;
    },
  },
  async mounted() {
    await Promise.all([
      this.getSchool(),
      this.getSchoolConfigs(1),
      this.getMedicaidDocumentationWorkflowEnabledParam(),
    ]);
  },
};
</script>
<style scoped>
.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #28a474 !important;
}

@media only screen and (max-width: 375px) {
  ::v-deep .vs__search {
    font-size: 12px !important;
  }
}
</style>
